import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { ChangelogEntry, EntryForm, CATEGORIES, EMPTY_FORM } from './changelogTypes';

export function useChangelogData() {
  const [entries, setEntries] = useState<ChangelogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<EntryForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [selectedEntries, setSelectedEntries] = useState<Set<string>>(new Set());

  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterPublished, setFilterPublished] = useState<string>('all');
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showNotification = useCallback((message: string, type: 'success' | 'error') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  }, []);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('admin_get_changelog_entries');
      if (error) throw error;
      setEntries(data || []);
    } catch (err: any) {
      showNotification(`Failed to load entries: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const filteredEntries = entries.filter(e => {
    if (filterCategory !== 'all' && e.category !== filterCategory) return false;
    if (filterPublished === 'published' && !e.is_published) return false;
    if (filterPublished === 'draft' && e.is_published) return false;
    return true;
  });

  const buildVersions = Array.from(new Set(entries.map(e => e.version))).sort((a, b) => {
    const numA = parseInt(a.replace(/[^0-9]/g, ''));
    const numB = parseInt(b.replace(/[^0-9]/g, ''));
    return (numB || 0) - (numA || 0);
  });

  const openCreateForm = () => {
    const latestVersion = buildVersions.length > 0 ? buildVersions[0] : '';
    setForm({ ...EMPTY_FORM, version: latestVersion });
    setEditingId(null);
    setShowForm(true);
  };

  const openEditForm = (entry: ChangelogEntry) => {
    setForm({
      version: entry.version,
      title: entry.title,
      description: entry.description,
      category: entry.category,
      is_published: entry.is_published,
    });
    setEditingId(entry.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.version.trim() || !form.title.trim()) {
      showNotification('Version and title are required', 'error');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const { data, error } = await supabase.rpc('admin_update_changelog_entry', {
          p_id: editingId,
          p_version: form.version.trim(),
          p_title: form.title.trim(),
          p_description: form.description.trim(),
          p_category: form.category,
          p_is_published: form.is_published,
        });
        if (error) throw error;
        if (data && !data.success) throw new Error(data.error);
        showNotification('Entry updated', 'success');
      } else {
        const { data, error } = await supabase.rpc('admin_create_changelog_entry', {
          p_version: form.version.trim(),
          p_title: form.title.trim(),
          p_description: form.description.trim(),
          p_category: form.category,
          p_is_published: form.is_published,
        });
        if (error) throw error;
        if (data && !data.success) throw new Error(data.error);
        showNotification('Entry created', 'success');
      }
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      await fetchEntries();
    } catch (err: any) {
      showNotification(`Save failed: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { data, error } = await supabase.rpc('admin_delete_changelog_entry', { p_id: id });
      if (error) throw error;
      if (data && !data.success) throw new Error(data.error);
      setEntries(prev => prev.filter(e => e.id !== id));
      setDeleteConfirm(null);
      showNotification('Entry deleted', 'success');
    } catch (err: any) {
      showNotification(`Delete failed: ${err.message}`, 'error');
    }
  };

  const handleBulkToggle = async (publish: boolean) => {
    if (selectedEntries.size === 0) return;
    try {
      const { data, error } = await supabase.rpc('admin_bulk_toggle_changelog_publish', {
        p_ids: Array.from(selectedEntries),
        p_is_published: publish,
      });
      if (error) throw error;
      showNotification(`${data?.count || selectedEntries.size} entries ${publish ? 'published' : 'unpublished'}`, 'success');
      setSelectedEntries(new Set());
      await fetchEntries();
    } catch (err: any) {
      showNotification(`Bulk action failed: ${err.message}`, 'error');
    }
  };

  const toggleSelection = (id: string) => {
    const next = new Set(selectedEntries);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedEntries(next);
  };


  const getCategoryInfo = (cat: string) => CATEGORIES.find(c => c.value === cat) || CATEGORIES[0];

  const groupedByVersion = filteredEntries.reduce<Record<string, ChangelogEntry[]>>((acc, entry) => {
    if (!acc[entry.version]) acc[entry.version] = [];
    acc[entry.version].push(entry);
    return acc;
  }, {});

  const sortedVersions = Object.keys(groupedByVersion).sort((a, b) => {
    const numA = parseInt(a.replace(/[^0-9]/g, ''));
    const numB = parseInt(b.replace(/[^0-9]/g, ''));
    return (numB || 0) - (numA || 0);
  });

  const toggleVersionGroup = (version: string) => {
    const next = new Set(selectedEntries);
    const allSelected = groupedByVersion[version].every(e => next.has(e.id));
    groupedByVersion[version].forEach(e => allSelected ? next.delete(e.id) : next.add(e.id));
    setSelectedEntries(next);
  };

  return {
    entries, loading, showForm, setShowForm, editingId, setEditingId, form, setForm, saving,
    deleteConfirm, setDeleteConfirm, selectedEntries, setSelectedEntries,
    filterCategory, setFilterCategory, filterPublished, setFilterPublished,
    notification, setNotification, fetchEntries, filteredEntries, buildVersions,
    openCreateForm, openEditForm, handleSave, handleDelete, handleBulkToggle,
    toggleSelection, toggleVersionGroup, getCategoryInfo, groupedByVersion, sortedVersions,
  };
}

export type ChangelogModel = ReturnType<typeof useChangelogData>;
